import MPCList, { MPCListProps, MPCListRef } from '@mpc/common/src/features/MPCList';
import FormDrawer from '@mpc/common/src/features/Refund/FormDrawer';
import { refundCheckRefundable, toRefundCheckParam } from '@mpc/common/src/features/Refund/api';
import { Message } from '@bedrock/components';
import React, { useMemo } from 'react';
import {
  ADVANCE_PRICE_CHECK_TAB_KEY,
  BIDDING_TAB_KEY,
  DEMAND_TAB_KEY,
  FIRST_LEVEL_ENUM,
  LIST_CONF,
  PREPAYMENT_TAB_KEY,
  RECHARGE_REFUND_TAB_KEY,
} from './utils';
import TopTabs from './TopTabs';
import OutRechargeForm from '../OutRechargeRefund/Recharge/Form';
import { showRechargeFormWithDraft } from '@mpc/common/src/features/RechargeRefund/Drawer/rechargeFormCommand';
import RechargeDetailDrawer from '../OutRechargeRefund/Recharge/Detail';
import { ajax, showAlert } from 'utils';
import {
  enums,
  outBiddingWorkbenchesList,
  outsourceCommonFilterOptions,
  outsourceMyWorkspaceGetDemandTodoList,
  outsourceMyWorkspaceGetPrepaymentSettledUploadList,
  outsourceMyWorkspaceGetPrepaymentAllList,
  outsourceV2AdvancePriceCheckList,
  outsourceMyWorkspaceGetRechargeRefundTodoList,
  outsourceMyWorkspaceGetRechargeRefundAllList,
} from 'services/api';
import { useMemoizedFn } from 'ahooks';
import _ from 'lodash';
import { Badge } from '@bedrock/components';
import { getKey, mpc_table_row_text_grey } from '@mpc/common/src/features/MPCList/utils';
import './index.less';
import moment from 'moment';
import { AdvancePriceCheck } from 'services/api';
import AdvancePriceCheckDetailDrawer from './AdvancePriceCheckDetail';

/** 运行时列表项可能携带 biz_id/biz_type（后端跨表返回），但自动生成类型未收录 */
type AdvancePriceCheckListItem = AdvancePriceCheck & {
  biz_id?: number | string;
  biz_type?: string;
};

export default function OutWorkbenchPortal(props: any) {
  const { menuCount, getMenuCount, childMenus, account } = props;
  const pathArr = props?.location?.pathname?.split('/') || [];
  const firstLevel = pathArr[2] as keyof typeof LIST_CONF;
  const secondLevel = pathArr[3] as
    | keyof (typeof LIST_CONF)[FIRST_LEVEL_ENUM.outNormalPurchase]
    | keyof (typeof LIST_CONF)[FIRST_LEVEL_ENUM.outBiddingPurchase];
  const listRef = React.useRef<MPCListRef>();
  /** 存储额外信息，目前用于存储各tab是我列排序信息 */
  const extRef = React.useRef<any>({});

  /** 单条充值/退款按钮 loading（行 id）；create 充值改命令式（先草稿校验），不再用 rechargeVisible 受控 */
  const [rechargeActionLoadingId, setRechargeActionLoadingId] = React.useState<number | null>(null);
  const [refundActionLoadingId, setRefundActionLoadingId] = React.useState<number | null>(null);
  /** 充值重新发起抽屉（待发起态退回后编辑）：复用 OutRechargeForm resubmit（带 recharge_order_id），字段可编辑 */
  const [rechargeEditVisible, setRechargeEditVisible] = React.useState(false);
  const [rechargeEditRow, setRechargeEditRow] = React.useState<any>(null);

  const refreshTable = useMemoizedFn(() => {
    listRef?.current?.tableRef.current?.queryCurrentPage?.();
    getMenuCount?.();
  });

  const showPrePayment = useMemo(() => {
    return account?.menus
      ?.find((menu: any) => menu?.key === 'outWorkbenchPortal')
      ?.children?.some((child: any) => child?.key === FIRST_LEVEL_ENUM.outPrepayment);
  }, [account]);

  /**
   * tab切换处理
   * @param key 菜单tabkey
   * @param level 菜单tab级别 默认 1
   * @returns
   */
  const handleTabsChange = (key: string, level: number = 1) => {
    const defaultSecTabKey = {
      [FIRST_LEVEL_ENUM.outNormalPurchase]: DEMAND_TAB_KEY.pendingQuote,
      [FIRST_LEVEL_ENUM.outBiddingPurchase]: BIDDING_TAB_KEY.toDoBidding,
      [FIRST_LEVEL_ENUM.outPrepayment]: PREPAYMENT_TAB_KEY.prepaymentUpload,
      [FIRST_LEVEL_ENUM.outAdvancePriceCheck]: ADVANCE_PRICE_CHECK_TAB_KEY.all,
      [FIRST_LEVEL_ENUM.outRechargeRefund]: RECHARGE_REFUND_TAB_KEY.todo,
    };
    if (level === 1) {
      pathArr[level + 1] = key;
      pathArr[level + 2] = defaultSecTabKey[key as keyof typeof defaultSecTabKey];
    } else {
      pathArr[level + 1] = key;
    }
    getMenuCount?.();
    props?.history?.push(pathArr.join('/') + props.location?.search);
  };

  const handleDealChange = (body: { finished: boolean; id: string }) => {
    ajax({
      url: 'outsource/v2/demands/processed',
      method: 'POST',
      body,
    }).then((res: any) => {
      showAlert({
        type: 'success',
        message: res.res_msg,
      });
      listRef.current?.refreshTable();
    });
  };

  const handleProcess = useMemoizedFn((record: AdvancePriceCheckListItem) => {
    AdvancePriceCheckDetailDrawer.show({
      record,
      onUploadSuccess: refreshTable,
      showUploadButton: record?.permit_actions?.can_upload,
    });
  });

  /**
   * 充值/退款单详情打开分流（16366-02）：按 order_type 命令式打开——
   * 退款走 FormDrawer.show（mode:'view' + isExternal 精简），充值走 RechargeDetailDrawer.show（withFunctionCall 命令式）。
   */
  // 退款抽屉实例引用：命令式 FormDrawer.show 每次新建实例，重复触发（行点击/按钮冒泡）会叠加两层，
  // 打开前先 close 上一个实例，保证同时只存在一个退款弹框
  const refundDrawerRef = React.useRef<{ close: () => void } | null>(null);

  const openRechargeRefundDetail = useMemoizedFn((record: any) => {
    if (record?.order_type === 'REFUND') {
      refundDrawerRef.current?.close();
      refundDrawerRef.current = FormDrawer.show({
        mode: 'view',
        orderId: Number(record?.id),
        isExternal: true,
        // 确认/退回/废弃成功后刷新列表（命令式实例不依赖路由，onSuccess 直刷）
        onSuccess: refreshTable,
      });
    } else if (record?.permit_actions?.can_abandon) {
      // 待发起态（供应商重新发起）：打开可编辑表单复用原单号，字段可编辑（对齐退款 FormDrawer edit 模式）
      setRechargeEditRow(record);
      setRechargeEditVisible(true);
    } else {
      RechargeDetailDrawer.show({
        orderId: Number(record?.id),
        onSuccess: refreshTable,
      });
    }
  });

  // @ts-ignore
  const currListConf = LIST_CONF?.[firstLevel]?.[secondLevel];

  const filterOptions = currListConf?.filterOptions || {};

  const customColumnsConf = currListConf?.customColumnsConf?.({
    handleDealChange,
    secondLevel,
    onProcess: handleProcess,
    // 发起充值：inline 打开抽屉（不跳路由），见 utils.tsx demandCustomColumnsConf
    onInitRecharge: async (record: any) => {
      // 发起充值：命令式（先调草稿校验返回才打开抽屉），按钮 loading
      const planDemandId = Number(record?.plan_demand_id);
      if (!planDemandId) return;
      setRechargeActionLoadingId(planDemandId);
      try {
        await showRechargeFormWithDraft({
          l2_demand_id: planDemandId,
          l3_demand_id: Number(record?.leaf_demand_id) || undefined,
          entry: 'SUPPLIER',
          isExternal: true,
          overseas: false,
          onSuccess: refreshTable,
        });
      } finally {
        setRechargeActionLoadingId(null);
      }
    },
    onInitRefund: async (record: any) => {
      // 需求列表发起退款：先校验能否发起（对齐 QuoteInfoTable:462），失败报错不开，按钮 loading
      const planDemandId = Number(record?.plan_demand_id);
      if (!planDemandId) return;
      setRefundActionLoadingId(planDemandId);
      try {
        // 退款校验：有三级单（leaf_demand_id）优先传 l3_demand_ids（走 checkRefundableByL3，带父 l2 校验），无三级单才传 l2_demand_ids
        const checkRes: any = await refundCheckRefundable(
          toRefundCheckParam(planDemandId, Number(record?.leaf_demand_id) || null)
        );
        const cr = checkRes?.data?.[0];
        if (!cr?.refundable) {
          Message.error(cr?.reason ?? '该单据当前不可发起退款');
          return;
        }
        // 校验通过 → inline 打开退款发起抽屉（不改变 URL，避免工作台状态丢失）；复用退款抽屉实例引用
        // 草稿接口需同时传二级单(l2)+三级单(l3)：plan_demand_id 恒为二级单、leaf_demand_id 为三级单
        // ⚠️ record.id 被 fetchList 覆盖成 leaf||plan，不可作 l2 用，否则草稿 channel_options/l1_category_id 查不到
        refundDrawerRef.current?.close();
        refundDrawerRef.current = FormDrawer.show({
          mode: 'create',
          l2DemandId: planDemandId,
          l3DemandId: Number(record?.leaf_demand_id) || undefined,
          isExternal: true,
          onSuccess: refreshTable,
        });
      } finally {
        setRefundActionLoadingId(null);
      }
    },
    // 充值/退款单详情：按 order_type 分流（退款命令式 FormDrawer.show 外包精简 / 充值 ?projectId 走充值抽屉）
    onDetail: openRechargeRefundDetail,
    // 操作列按钮防重复点击（loading id 传 utils，用 disabled 而非 loading，避免图标占位致列表抖动）
    rechargeActionLoadingId,
    refundActionLoadingId,
  });

  // const items = firstLevel === FIRST_LEVEL_ENUM.outBiddingPurchase ? biddingTabItems : demandTabItems;
  const items =
    firstLevel === FIRST_LEVEL_ENUM.outAdvancePriceCheck
      ? undefined
      : childMenus?.map?.((menu: { name: any; key: string }) => {
          const badge = menuCount?.find((countInfo: any) => countInfo.key === menu.key)?.count;
          return {
            ...menu,
            tab: (
              <div>
                {menu.name}
                <Badge count={badge} className="m-l-8" />
              </div>
            ),
          };
        }) || [];

  const fetchList: MPCListProps['fetchList'] = (params) => {
    // 充值/退款列表（16366-03 T30）：优先消费 LIST_CONF 配置的 fetchListApi（过渡期复用 recharge_refund 合并三态接口，
    // order_type_list 按 firstLevel 分流充值 RECHARGE / 退款 REFUND；后端 T25 部署 + genapi 后切换为 supplierRechargePage/supplierRefundPage）
    const rechargeRefundConf = currListConf as {
      fetchListApi?: (data: Record<string, any>) => Promise<any>;
      orderTypeList?: string[];
    };
    if (rechargeRefundConf?.fetchListApi) {
      const { conditions } = params;
      return rechargeRefundConf
        .fetchListApi({
          ...params,
          ...conditions,
          order_type_list: rechargeRefundConf.orderTypeList,
          limit: params.pageSize,
        })
        .then((res: any) => {
          const list = res?.data?.list || [];
          return {
            data: {
              ...res?.data,
              list,
            },
          };
        });
    }
    if (firstLevel === FIRST_LEVEL_ENUM.outNormalPurchase) {
      const todo_type = _.snakeCase(secondLevel?.replace('Portal', '') || '').toUpperCase() as any;
      const { conditions } = params;
      return outsourceMyWorkspaceGetDemandTodoList({
        ...params,
        ...conditions,
        quote_deadline_range:
          conditions?.quote_deadline_range === null
            ? undefined
            : conditions?.quote_deadline_range?.map((item: any) => moment(item).format('YYYY-MM-DD HH:mm:ss')),
        limit: params.pageSize,
        todo_type,
        // state: (_.snakeCase('pendingQuote')).toUpperCase() as any,
      }).then((res: any) => {
        const list = res?.data?.list || [];
        return {
          data: {
            ...res?.data,
            list: list.map((item: any) => ({ ...item, id: item?.leaf_demand_id || item?.plan_demand_id })),
          },
        };
      });
    }
    if (firstLevel === FIRST_LEVEL_ENUM.outPrepayment) {
      const { conditions } = params;
      const sort_param = extRef.current?.[secondLevel];
      let fetchListApi = outsourceMyWorkspaceGetPrepaymentSettledUploadList;
      if (secondLevel && (secondLevel as string) === PREPAYMENT_TAB_KEY.allPrepayment) {
        fetchListApi = outsourceMyWorkspaceGetPrepaymentAllList;
      }
      const newConditions = {
        ...conditions,
        sort_param,
        status_list: conditions?.status_list,
      };
      return fetchListApi({
        ...params,
        ...newConditions,
        limit: params.pageSize,
      }).then((res: any) => {
        const list = res?.data?.list || [];
        return {
          data: {
            ...res?.data,
            list: list,
          },
        };
      });
    }
    if (firstLevel === FIRST_LEVEL_ENUM.outAdvancePriceCheck) {
      const { conditions } = params;
      const newConditions = {
        demand_id: conditions?.search_text,
        product_ids: conditions?.product_ids,
        price_check_states: conditions?.price_check_states,
      }
      return outsourceV2AdvancePriceCheckList({
        ...params,
        ...newConditions,
      });
    }
    if (firstLevel === FIRST_LEVEL_ENUM.outRechargeRefund) {
      const { conditions } = params;
      const tabMap = {
        [RECHARGE_REFUND_TAB_KEY.todo]: 'todo',
        [RECHARGE_REFUND_TAB_KEY.all]: 'all',
      };
      const fetchApi = {
        todo: outsourceMyWorkspaceGetRechargeRefundTodoList,
        all: outsourceMyWorkspaceGetRechargeRefundAllList,
      }[tabMap[secondLevel as RECHARGE_REFUND_TAB_KEY]];
      return fetchApi({ ...params, ...conditions, limit: params.pageSize }).then((res: any) => ({
        data: { ...res?.data, list: res?.data?.list || [] },
      }));
    }
    const bidding_states_map = {
      [BIDDING_TAB_KEY.toDoBidding]: enums.BiddingProcessStageEnum.TO_DO_PROCESS,
      [BIDDING_TAB_KEY.doneBidding]: enums.BiddingProcessStageEnum.COMPLETED_PROCESS,
    };
    return outBiddingWorkbenchesList({
      ...params,
      size: params.pageSize,
      bidding_process_stage: bidding_states_map[secondLevel as BIDDING_TAB_KEY],
    });
  };

  const fetchFilterOptions: MPCListProps['fetchFilterOptions'] = () => {
    const params =
      firstLevel === FIRST_LEVEL_ENUM.outNormalPurchase
        ? {
            outsource_todo_type: _.snakeCase(secondLevel?.replace('Portal', '') || '').toUpperCase() as any,
          }
        : {};
    return outsourceCommonFilterOptions({
      ...params,
      keys: filterOptions?.filterKeys?.filter((el: any) => !el.noFetchOption)?.map?.((key: any) => getKey(key, 'optionKey')),
    });
  };

  const toolbarSearch = useMemo(() => {
    if (firstLevel === FIRST_LEVEL_ENUM.outPrepayment) {
      return {
        placeholder: '请输入预付单号/来源单号/需求标题',
        id: 'search_text',
        style: {
          width: 280,
        },
      };
    }
    if (firstLevel === FIRST_LEVEL_ENUM.outAdvancePriceCheck) {
      return {
        placeholder: '请输入单号',
        id: 'search_text',
        style: {
          width: 280,
        },
      };
    }
    // 充值/退款单号搜索框：值走 search_text，外包端 fetchList 透传 conditions.search_text → form.searchText
    if (firstLevel === FIRST_LEVEL_ENUM.outRechargeRefund) {
      return {
        placeholder: '请输入充值/退款单号',
        id: 'search_text',
        style: {
          width: 280,
        },
      };
    }
    return undefined;
  }, [firstLevel]);

  return (
    <>
      <MPCList
        warpperClassName={`${
          firstLevel === FIRST_LEVEL_ENUM.outAdvancePriceCheck ? 'advance-price-check-warpper' : ''
        }`}
        group_type={firstLevel}
        // 达人报价 Tab：参考预付单的行点击打开抽屉的交互，点击行直接打开处理抽屉；
        // 其他 Tab 保持 MPCList 默认行为（修改 URL → 路由匹配打开对应 Drawer）。
        onClickRow={
          firstLevel === FIRST_LEVEL_ENUM.outAdvancePriceCheck
            ? (record: AdvancePriceCheckListItem) => handleProcess(record)
            : firstLevel === FIRST_LEVEL_ENUM.outRechargeRefund
              ? (record: any) => openRechargeRefundDetail(record)
              : undefined
        }
        menuKey={
          firstLevel === FIRST_LEVEL_ENUM.outNormalPurchase
            ? _.snakeCase(secondLevel?.replace('Portal', '') || '').toUpperCase()
            : undefined
        }
        HeaderPlace={
          <TopTabs
            activeKey={firstLevel}
            onChange={handleTabsChange}
            className={`${firstLevel === FIRST_LEVEL_ENUM.outAdvancePriceCheck ? 'mb-4' : ''}`}
            items={[
              {
                key: FIRST_LEVEL_ENUM.outNormalPurchase,
                name: '常规采购',
                badge: menuCount?.find((countInfo: any) => countInfo.key === FIRST_LEVEL_ENUM['outNormalPurchase'])
                  ?.count,
              },
              {
                key: FIRST_LEVEL_ENUM.outBiddingPurchase,
                name: '比选',
                badge: menuCount?.find((countInfo: any) => countInfo.key === FIRST_LEVEL_ENUM['outBiddingPurchase'])
                  ?.count,
              },
              showPrePayment && {
                key: FIRST_LEVEL_ENUM.outPrepayment,
                name: '预付单',
                badge: menuCount?.find((countInfo: any) => countInfo.key === FIRST_LEVEL_ENUM['outPrepayment'])?.count,
              },
              {
                key: FIRST_LEVEL_ENUM.outAdvancePriceCheck,
                name: '达人报价',
                badge: menuCount?.find((countInfo: any) => countInfo.key === FIRST_LEVEL_ENUM['outAdvancePriceCheck'])
                  ?.count,
              },
              {
                key: FIRST_LEVEL_ENUM.outRechargeRefund,
                name: '充值/退款单',
                badge: menuCount?.find((countInfo: any) => countInfo.key === FIRST_LEVEL_ENUM.outRechargeRefund)?.count,
              },
            ].filter(Boolean)}
          />
        }
        filterOptions={filterOptions}
        fetchFilterOptions={fetchFilterOptions}
        customColumnsConf={customColumnsConf}
        fetchList={fetchList}
        // @ts-ignore
        ref={listRef}
        // @ts-ignore
        ProTablePlusProps={{
          key: firstLevel,
          onSortChange(dataIndex, sortOrder) {
            const sort_param = sortOrder ? { sort_field: dataIndex, is_asc: sortOrder === 'ascend' } : undefined;
            extRef.current = {
              ...extRef.current,
              [secondLevel]: sort_param,
            };
            refreshTable();
          },
          toolbar: {
            search: toolbarSearch,
            tabSize: 'medium',
            filterSetting: {
              defaultKeys: filterOptions?.filterKeys?.map((key: any) => getKey(key, 'searchKey')),
              disableAddButton: true,
              dataStore: {
                key: location.pathname + `_${firstLevel}_${secondLevel}_with-condition`,
                fromThe: 'localStorage',
                toThe: 'localStorage',
              },
            },
            tabs: {
              defaultActiveKey: secondLevel,
              onChange: (key) => handleTabsChange(key, 2),
              items,
            },
          },
          rowClassName: (r: any, i: number) => {
            if (
              firstLevel === FIRST_LEVEL_ENUM.outNormalPurchase &&
              ['REJECTED', 'NOT_QUOTED', 'CANCELED', 'PAYED', 'NOT_SELECTED'].includes(r.state || '')
            ) {
              return mpc_table_row_text_grey;
            }
            if (firstLevel === FIRST_LEVEL_ENUM.outPrepayment && ['ABANDONED', 'COMPLETED'].includes(r.status || '')) {
              return mpc_table_row_text_grey;
            }
            return '';
          },
        }}
      />
      <OutRechargeForm
        visible={rechargeEditVisible}
        onClose={() => {
          setRechargeEditVisible(false);
          setRechargeEditRow(null);
        }}
        onOk={() => {
          setRechargeEditVisible(false);
          setRechargeEditRow(null);
          refreshTable();
        }}
        l2_demand_id={rechargeEditRow?.second_demand_id ?? 0}
        l3_demand_id={rechargeEditRow?.third_demand_id ?? undefined}
        recharge_order_id={rechargeEditRow?.id != null ? Number(rechargeEditRow.id) : undefined}
        overseas={false}
      />
    </>
  );
}
